import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useCurrentOrganization } from '../../stores/auth';
import { useGroupStore } from '../../stores/group';
import { extractApiError } from '../../lib/api';
import { useModalStore } from '../../stores/modal';
import { getDashboardStats, getDashboardTrend, DashboardTrendData } from '../../lib/organizations';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { DashboardStatsWithLoading, DashboardChartData, DashboardData, ReportTypeStat } from './types';
import { DateRange, getPresetRange } from './components/DateRangeFilter';
import CreateInspectionModal from '../../components/modals/CreateInspectionModal';
import SiteFormModal from '../../components/modals/SiteFormModal';
import { Inspection, getLocalDateString } from '../../lib/inspections';

const DashboardMobile = lazy(() => import('./DashboardMobile'));
const DashboardDesktop = lazy(() => import('./DashboardDesktop'));

/**
 * 대시보드 페이지
 *
 * ResponsivePage 패턴을 적용하여 모바일/데스크톱 레이아웃을 분리합니다.
 */
export default function Dashboard() {
  const layout = useDeviceLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const currentOrganization = useCurrentOrganization();
  const { openCreateInspection } = useModalStore();
  const currentGroupId = useGroupStore((s) => s.currentGroupId);
  const companyId = currentOrganization?.companyId;
  const isMobile = layout === 'phone' || layout === 'fold';

  // 기간 필터 상태 (기본: 1주일)
  const [dateRange, setDateRange] = useState<DateRange>(() => getPresetRange('week'));

  // 대시보드 통계 상태
  const [stats, setStats] = useState<DashboardStatsWithLoading>({
    sitesCount: 0,
    inProgressInspections: 0,
    completedInspections: 0,
    scheduledInspections: 0,
    pendingReports: 0,
    approvedReports: 0,
    unresolvedFindings: 0,
    resolvedFindings: 0,
    reportTypeStats: [],
    isLoading: true,
  });

  // 차트 데이터 상태
  const [chartData, setChartData] = useState<DashboardChartData>({
    resolvedFindings: 0,
    reportTypeStats: [],
    isLoading: true,
  });

  // 트렌드 데이터 상태
  const [trendData, setTrendData] = useState<DashboardTrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(true);

  // 통계 + 차트 + 트렌드 데이터 로드
  useEffect(() => {
    if (!companyId) {
      setStats((prev) => ({ ...prev, isLoading: false, error: undefined }));
      setChartData((prev) => ({ ...prev, isLoading: false }));
      setTrendLoading(false);
      return;
    }

    const { dateFrom, dateTo } = dateRange;

    const loadStats = async () => {
      try {
        const data = await getDashboardStats(companyId, dateFrom, dateTo, currentGroupId);
        setStats({ ...data, isLoading: false, error: undefined });

        // 서버에서 집계된 보고서 유형(양식)별 통계를 차트 데이터에 반영. 라벨은 양식 이름 → 양식 키 →
        // '양식 미지정'(templateKey 없는 옛 행) 순 — 유형 레이블 맵을 두지 않는다.
        const reportTypeStats: ReportTypeStat[] = (data.reportTypeStats ?? []).map((r) => ({
          templateKey: r.templateKey,
          label: r.templateName?.trim() || r.templateKey || '양식 미지정',
          count: r.count,
        }));
        setChartData({
          resolvedFindings: 0,
          reportTypeStats,
          isLoading: false,
        });
      } catch (err) {
        // 실패한 수치를 0으로 그리지 않는다 — 레이아웃이 error를 보고 '—'로 둔다.
        setStats((prev) => ({ ...prev, isLoading: false, error: extractApiError(err).message }));
        setChartData((prev) => ({ ...prev, isLoading: false }));
      }
    };

    const loadTrend = async () => {
      setTrendLoading(true);
      try {
        const data = await getDashboardTrend(companyId, dateFrom, dateTo, currentGroupId);
        setTrendData(data);
      } catch {
        setTrendData(null);
      } finally {
        setTrendLoading(false);
      }
    };

    setStats((prev) => ({ ...prev, isLoading: true, error: undefined }));
    setChartData((prev) => ({ ...prev, isLoading: true }));

    loadStats();
    loadTrend();
  }, [companyId, dateRange, currentGroupId]);

  // 기간 변경 핸들러
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
  }, []);

  // 헤더 부제 결정 — 제목은 '대시보드'로 고정이라 조직 맥락과 환영 문구를 이 줄이 받는다.
  const getDashboardSubtitle = () => {
    const welcome = user?.name ? `${user.name}님, 환영합니다!` : '환영합니다!';
    if (!currentOrganization) return welcome;
    return `${currentOrganization.company.name} · ${welcome}`;
  };

  // 레이아웃에 전달할 데이터
  const data: DashboardData = {
    stats,
    chartData,
    trendData,
    trendLoading,
    currentOrganization,
    dashboardSubtitle: getDashboardSubtitle(),
    dateRange,
    onDateRangeChange: handleDateRangeChange,
  };

  // 레이아웃 선택
  const Layout = isMobile ? DashboardMobile : DashboardDesktop;

  // 새 점검 모달 열기 핸들러
  const handleStartNewClick = () => {
    openCreateInspection();
  };

  // 대시보드에서 점검 예약 성공 시 스케줄 페이지의 해당 날짜로 이동
  const handleInspectionCreated = (inspection: Inspection) => {
    if (inspection.scheduledAt) {
      const dateStr = getLocalDateString(new Date(inspection.scheduledAt));
      navigate(`/schedules?date=${dateStr}`);
    } else {
      navigate('/schedules');
    }
  };

  return (
    <>
      <Suspense fallback={<div className="flex items-center justify-center h-64 text-ink-400">불러오는 중...</div>}>
        <Layout data={data} layout={layout} onStartNewClick={handleStartNewClick} />
      </Suspense>

      {/* 점검 생성 모달 */}
      <CreateInspectionModal onSuccess={handleInspectionCreated} />

      {/* 현장 생성 모달 — source 기반으로 SiteFormModal 내부에서 inspection 재오픈 처리 */}
      <SiteFormModal
        onSuccess={() => {
          if (companyId) {
            // 접두사 매칭이라 ['sites', companyId, groupId?] 캐시가 모두 무효화된다.
            queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
          }
        }}
      />
    </>
  );
}
